import type { AdFormat, Provider, ProviderGroup } from '@/types/formats';
import { Globe, Search, Tv, Star, Shield } from 'lucide-react';

// Mooncloak Native Formats
// Based on search-ads placement slots
export const MOONCLOAK_FORMATS: AdFormat[] = [
  // Search Campaign Formats
  {
    id: 'mc-search-top',
    name: 'Search Top',
    width: 728,
    height: 90,
    provider: 'mooncloak',
    platforms: 'Mooncloak Search',
    category: 'Search',
  },
  {
    id: 'mc-search-inline',
    name: 'Search Inline',
    width: 600,
    height: 100,
    provider: 'mooncloak',
    platforms: 'Mooncloak Search',
    category: 'Search',
  },
  {
    id: 'mc-search-sidebar',
    name: 'Search Sidebar',
    width: 300,
    height: 250,
    provider: 'mooncloak',
    platforms: 'Mooncloak Search',
    category: 'Search',
  },
  // Homepage Campaign Formats
  {
    id: 'mc-homepage-hero',
    name: 'Homepage Hero',
    width: 1200,
    height: 400,
    provider: 'mooncloak',
    platforms: 'Mooncloak Homepage',
    category: 'Homepage',
  },
  {
    id: 'mc-homepage-featured',
    name: 'Homepage Featured',
    width: 600,
    height: 400,
    provider: 'mooncloak',
    platforms: 'Mooncloak Homepage',
    category: 'Homepage',
  },
  // GameDay Campaign Formats
  {
    id: 'mc-gameday-hero',
    name: 'GameDay Hero',
    width: 1200,
    height: 628,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  {
    id: 'mc-gameday-sidebar',
    name: 'GameDay Sidebar',
    width: 300,
    height: 600,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  {
    id: 'mc-gameday-native',
    name: 'GameDay Native Card',
    width: 400,
    height: 300,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  {
    id: 'mc-gameday-food',
    name: 'GameDay Food & Drinks',
    width: 350,
    height: 280,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  {
    id: 'mc-gameday-bars',
    name: 'GameDay Bars',
    width: 350,
    height: 280,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  {
    id: 'mc-gameday-betting',
    name: 'GameDay Betting',
    width: 320,
    height: 100,
    provider: 'mooncloak',
    platforms: 'Mooncloak GameDay',
    category: 'GameDay',
  },
  // Display Campaign Formats
  {
    id: 'mc-display-banner',
    name: 'Display Banner',
    width: 970,
    height: 250,
    provider: 'mooncloak',
    platforms: 'Mooncloak Display',
    category: 'Display',
  },
  {
    id: 'mc-display-rectangle',
    name: 'Display Rectangle',
    width: 336,
    height: 280,
    provider: 'mooncloak',
    platforms: 'Mooncloak Display',
    category: 'Display',
  },
];

// Facebook Formats
export const FACEBOOK_FORMATS: AdFormat[] = [
  {
    id: 'fb-feed',
    name: 'Feed Post',
    width: 1200,
    height: 628,
    provider: 'facebook',
    platforms: 'Facebook Feed',
    category: 'Feed',
  },
  {
    id: 'fb-story',
    name: 'Story',
    width: 1080,
    height: 1920,
    provider: 'facebook',
    platforms: 'Facebook Story',
    category: 'Story',
  },
  {
    id: 'fb-carousel',
    name: 'Carousel',
    width: 1080,
    height: 1080,
    provider: 'facebook',
    platforms: 'Facebook Carousel',
    category: 'Feed',
  },
  {
    id: 'fb-cover',
    name: 'Page Cover',
    width: 820,
    height: 312,
    provider: 'facebook',
    platforms: 'Facebook Page Cover',
    category: 'Cover',
  },
];

// Google Display Formats
export const GOOGLE_FORMATS: AdFormat[] = [
  {
    id: 'google-leaderboard',
    name: 'Leaderboard',
    width: 728,
    height: 90,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Banner',
  },
  {
    id: 'google-mpu',
    name: 'MPU',
    width: 300,
    height: 250,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Rectangle',
  },
  {
    id: 'google-wide-skyscraper',
    name: 'Wide Skyscraper',
    width: 160,
    height: 600,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Skyscraper',
  },
  {
    id: 'google-half-page',
    name: 'Half Page',
    width: 300,
    height: 600,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Rectangle',
  },
  {
    id: 'google-billboard',
    name: 'Billboard',
    width: 970,
    height: 250,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Banner',
  },
  {
    id: 'google-large-rectangle',
    name: 'Large Rectangle',
    width: 336,
    height: 280,
    provider: 'google',
    platforms: 'Google Display Network',
    category: 'Rectangle',
  },
];

// Blip Billboard Formats
export const BLIP_FORMATS: AdFormat[] = [
  {
    id: 'blip-horizontal',
    name: 'Horizontal Billboard',
    width: 1920,
    height: 1080,
    provider: 'blip',
    platforms: 'Blip Digital Billboard',
    category: 'Billboard',
  },
  {
    id: 'blip-vertical',
    name: 'Vertical Display',
    width: 1080,
    height: 1920,
    provider: 'blip',
    platforms: 'Blip Digital Billboard',
    category: 'Billboard',
  },
  {
    id: 'blip-square',
    name: 'Square Display',
    width: 1080,
    height: 1080,
    provider: 'blip',
    platforms: 'Blip Digital Billboard',
    category: 'Billboard',
  },
];

// Influencer / Social Media Formats
export const INFLUENCER_FORMATS: AdFormat[] = [
  {
    id: 'ig-feed',
    name: 'Instagram Feed',
    width: 1080,
    height: 1350,
    provider: 'influencer',
    platforms: 'Instagram',
    category: 'Feed',
  },
  {
    id: 'ig-story',
    name: 'Instagram Story',
    width: 1080,
    height: 1920,
    provider: 'influencer',
    platforms: 'Instagram Story',
    category: 'Story',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    width: 1080,
    height: 1920,
    provider: 'influencer',
    platforms: 'TikTok',
    category: 'Video',
  },
  {
    id: 'youtube-thumb',
    name: 'YouTube Thumbnail',
    width: 1280,
    height: 720,
    provider: 'influencer',
    platforms: 'YouTube',
    category: 'Thumbnail',
  },
  {
    id: 'twitter-post',
    name: 'Twitter Post',
    width: 1200,
    height: 675,
    provider: 'influencer',
    platforms: 'Twitter/X',
    category: 'Feed',
  },
];

// Provider Groups
export const PROVIDERS: ProviderGroup[] = [
  {
    id: 'mooncloak',
    name: 'Mooncloak',
    icon: Shield,
    formats: MOONCLOAK_FORMATS,
  },
  {
    id: 'facebook',
    name: 'Facebook',
    icon: Globe,
    formats: FACEBOOK_FORMATS,
  },
  {
    id: 'google',
    name: 'Google Display',
    icon: Search,
    formats: GOOGLE_FORMATS,
  },
  {
    id: 'blip',
    name: 'Blip Billboards',
    icon: Tv,
    formats: BLIP_FORMATS,
  },
  {
    id: 'influencer',
    name: 'Influencer',
    icon: Star,
    formats: INFLUENCER_FORMATS,
  },
];

// All formats flat list
export const ALL_FORMATS: AdFormat[] = [
  ...MOONCLOAK_FORMATS,
  ...FACEBOOK_FORMATS,
  ...GOOGLE_FORMATS,
  ...BLIP_FORMATS,
  ...INFLUENCER_FORMATS,
];

// Legacy format mapping for backwards compatibility
export const LEGACY_FORMATS: Record<string, AdFormat> = {
  'square-1080': {
    id: 'square-1080',
    name: 'Square 1:1',
    width: 1080,
    height: 1080,
    provider: 'influencer',
    platforms: 'Instagram · FB · Google Display',
  },
  'landscape-1200': {
    id: 'landscape-1200',
    name: 'Landscape 1.91:1',
    width: 1200,
    height: 628,
    provider: 'facebook',
    platforms: 'FB / Google link preview',
  },
  'story-1080': {
    id: 'story-1080',
    name: 'Story 9:16',
    width: 1080,
    height: 1920,
    provider: 'influencer',
    platforms: 'IG/FB/TikTok Story · vertical Blip',
  },
  'wide-1920': {
    id: 'wide-1920',
    name: 'Wide 16:9',
    width: 1920,
    height: 1080,
    provider: 'blip',
    platforms: 'YouTube · horizontal billboard · Blip',
  },
  'leaderboard': {
    id: 'leaderboard',
    name: 'Leaderboard 728×90',
    width: 728,
    height: 90,
    provider: 'google',
    platforms: 'Google Display',
  },
  'mpu': {
    id: 'mpu',
    name: 'MPU 300×250',
    width: 300,
    height: 250,
    provider: 'google',
    platforms: 'Google Display',
  },
  'skyscraper': {
    id: 'skyscraper',
    name: 'Skyscraper 300×600',
    width: 300,
    height: 600,
    provider: 'google',
    platforms: 'Google Display',
  },
};

// Helper functions
export function getFormatById(id: string): AdFormat | undefined {
  return ALL_FORMATS.find(f => f.id === id) || LEGACY_FORMATS[id];
}

export function getFormatsByProvider(provider: Provider): AdFormat[] {
  return ALL_FORMATS.filter(f => f.provider === provider);
}

export function getProviderById(id: Provider): ProviderGroup | undefined {
  return PROVIDERS.find(p => p.id === id);
}

export function createCustomFormat(
  name: string,
  width: number,
  height: number,
  platforms: string = 'Custom'
): AdFormat {
  return {
    id: `custom-${width}x${height}-${Date.now()}`,
    name,
    width,
    height,
    provider: 'custom',
    platforms,
  };
}

// Default format
export const DEFAULT_FORMAT = LEGACY_FORMATS['square-1080'];
